/**
 * Marcas a revisión.
 *
 * El backend manda a `PENDING_APPROVAL` lo que no puede dar por bueno pero
 * tampoco tirar: un fichaje de hace más de 48 h, uno fuera de la geovalla en
 * diferido, uno que reclama un centro que no resuelve, un QR que no valida.
 * Una marca a revisión NO cuenta para el cálculo del día, así que mientras
 * nadie la mire el informe del art. 34.9 imprime AUSENTE una jornada que se
 * trabajó y la bolsa de horas descuenta.
 *
 * `GET /punch/pending` y `POST /punch/pending/{id}/resolve` existían desde el
 * 23/08/2026 y no los llamaba nadie: cuatro puertas escribiendo en una cola
 * que ninguna pantalla enseñaba. Esta es esa pantalla.
 *
 * Confirmar la vuelve VALID y recalcula el día. Descartar la deja CANCELLED:
 * la marca no se borra nunca.
 */
import { useCallback, useEffect, useState } from 'react';
import { AdminLayout } from './AdminLayout';
import {
  getPendingPunches,
  resolvePendingPunch,
  PendingPunch,
  apiMessage,
} from '../../services/apiClient';
import { Icon } from '../ui/Icon';

const TYPE_LABEL: Record<string, string> = {
  IN: 'Entrada',
  OUT: 'Salida',
  BREAK_START: 'Inicio de pausa',
  BREAK_END: 'Fin de pausa',
};

const SOURCE_LABEL: Record<string, string> = {
  APP: 'App del trabajador',
  WEB: 'Navegador',
  KIOSK: 'Quiosco',
  // r2b20[6] (ronda 4): ADMIN y TERMINAL no existen en el ENUM `PunchSource`
  // (WEB/APP/KIOSK/SYSTEM) — etiquetas fantasma que sólo engañaban al
  // mantenedor; SYSTEM (marca autogenerada) caía al fallback en inglés.
  SYSTEM: 'Sistema',
};

const STATUS_LABEL: Record<string, string> = {
  VALID: 'válida',
  PENDING_APPROVAL: 'a revisión',
  CANCELLED: 'anulada',
};

// r2b02[2] (mitad frontend): por qué cada marca acabó aquí. Son los códigos
// que ya existen en el backend (batch_sync/punch_processor/geofencing);
// cualquiera que no esté en el mapa se pinta tal cual — prefimos el código
// crudo a un «motivo genérico» que no diga nada.
const REVIEW_REASON_LABEL: Record<string, string> = {
  BACKDATED: 'hora declarada fuera de rango',
  PRL_BLOCKED: 'documentación PRL',
  SALIDA_SIN_ENTRADA_VALIDA: 'salida sin entrada confirmada',
  // Transiciones de la máquina de estados: la marca descolgada con la
  // jornada (salida sin entrada, segunda entrada, pausa sin jornada…).
  NOT_CLOCKED_IN: 'secuencia de marcas',
  ALREADY_CLOCKED_IN: 'secuencia de marcas',
  NOT_ON_BREAK: 'secuencia de marcas',
  ALREADY_ON_BREAK: 'secuencia de marcas',
  INVALID_TRANSITION: 'secuencia de marcas',
};

function motivoRevision(code: string): string {
  if (REVIEW_REASON_LABEL[code]) return REVIEW_REASON_LABEL[code];
  // *_GEOFENCE y variantes de geovalla en diferido.
  if (code.toUpperCase().includes('GEOFENCE')) return 'fuera de geovalla (diferido)';
  return code;
}

function fechaHora(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })} · ${d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`;
}

function hora(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

export function PendingPunchInbox() {
  const [items, setItems] = useState<PendingPunch[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [discarding, setDiscarding] = useState<number | null>(null);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    setItems(await getPendingPunches());
  }, []);

  useEffect(() => {
    load().catch(() => setError('No se pudieron cargar las marcas a revisión'));
  }, [load]);

  const resolve = async (id: number, approved: boolean, motivo?: string) => {
    setError(null);
    try {
      await resolvePendingPunch(id, approved, motivo);
      setDiscarding(null);
      setReason('');
      await load();
    } catch (e: unknown) {
      // r2b20[4]: el 409 de resolve trae la instrucción («el trabajador
      // volvió a fichar alrededor de esta marca…»); tragarlo dejaba un
      // «No se pudo resolver la marca» sin pista, reintentar daba lo mismo.
      // Mismo patrón que TimeCorrectionInbox/AnomalyInbox.
      setError(apiMessage(e, 'No se pudo resolver la marca'));
    }
  };

  return (
    <AdminLayout
      title="Marcas a revisión"
      subtitle="Fichajes registrados que todavía no cuentan para el cálculo del día"
    >
      <div className="max-w-[920px] space-y-3.5">
        <div className="flex items-center gap-2.5 rounded-xl bg-info-soft border border-info px-4 py-3 text-[14px] text-info">
          <Icon name="info" size={18} strokeWidth={1.8} className="shrink-0" />
          Mientras estén aquí, estas jornadas salen AUSENTES en el informe de Inspección.
          Confirmar recalcula el día; descartar anula la marca sin borrarla.
        </div>

        {error && <p className="rounded-xl bg-danger-soft text-danger px-4 py-3 text-[14px]">{error}</p>}

        {items.map((p) => (
          <div key={p.id} className="card-surface p-5 flex gap-4 items-center flex-wrap">
            <span className="w-10 h-10 rounded-lg bg-surface-2 grid place-items-center font-display font-bold text-ink-2 shrink-0">
              {p.user_name
                .split(' ')
                .map((w) => w[0])
                .slice(0, 2)
                .join('')}
            </span>
            <div className="flex-1 min-w-[200px]">
              <p className="font-semibold text-[15.5px]">{p.user_name}</p>
              <p className="font-mono text-[13px] text-ink-2 mt-0.5">
                {TYPE_LABEL[p.type] ?? p.type} · {fechaHora(p.server_timestamp)}
                {p.worksite_name && <> · {p.worksite_name}</>}
              </p>
              <p className="text-[12.5px] text-ink-3 mt-0.5">
                {SOURCE_LABEL[p.source] ?? p.source}
                {p.client_timestamp && p.client_timestamp !== p.server_timestamp && (
                  <> · el dispositivo dijo {fechaHora(p.client_timestamp)}</>
                )}
              </p>
              {p.review_reason && (
                <p className="text-[12px] text-warn mt-1.5">
                  A revisión: {motivoRevision(p.review_reason)}
                </p>
              )}
            </div>

            {p.marcas_posteriores.length > 0 && (
              <div className="w-full rounded-xl bg-surface-2 border border-line px-4 py-2.5">
                <p className="text-[11.5px] font-semibold text-ink-3 uppercase tracking-wide">
                  Marcas posteriores de {p.user_name.split(' ')[0]}
                </p>
                <ul className="mt-1 space-y-0.5">
                  {p.marcas_posteriores.map((m, i) => (
                    <li key={i} className="font-mono text-[12.5px] text-ink-2">
                      {hora(m.server_timestamp)} · {TYPE_LABEL[m.type] ?? m.type} ·{' '}
                      <span className={m.status === 'VALID' ? 'text-ink-3' : 'text-warn'}>
                        {STATUS_LABEL[m.status] ?? m.status}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {discarding === p.id ? (
              <div className="w-full flex flex-wrap gap-2.5 items-end">
                <label className="block flex-1 min-w-[220px]">
                  <span className="label">Motivo (queda en la auditoría)</span>
                  <input className="field" value={reason} onChange={(e) => setReason(e.target.value)} />
                </label>
                <button
                  onClick={() => setDiscarding(null)}
                  className="h-12 px-4 rounded-xl border border-line-strong font-semibold text-[14px]"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => resolve(p.id, false, reason.trim())}
                  disabled={reason.trim().length < 5}
                  className="h-12 px-5 rounded-xl bg-danger text-on-danger font-bold text-[14px] disabled:opacity-40"
                >
                  Confirmar descarte
                </button>
              </div>
            ) : (
              <div className="flex gap-2.5 shrink-0">
                <button
                  onClick={() => setDiscarding(p.id)}
                  className="h-10 px-4 rounded-xl border border-line-strong text-danger font-semibold text-[14px]"
                >
                  Descartar
                </button>
                <button
                  onClick={() => resolve(p.id, true)}
                  className="h-10 px-5 rounded-xl bg-brand text-on-brand font-bold text-[14px]"
                >
                  Confirmar
                </button>
              </div>
            )}
          </div>
        ))}

        {items.length === 0 && !error && (
          <p className="text-center text-[14px] text-ink-3 py-12">Sin marcas a revisión ✓</p>
        )}
      </div>
    </AdminLayout>
  );
}
